/**
 * 任务类型定义
 * 用于定义队列中任务的基本结构
 */
export interface Task {
  /** 任务唯一标识符 */
  id: string;
  /** 任务延时时间（毫秒） */
  delay?: number;
  /** 任务执行函数 */
  execute: () => Promise<void>;
}

/**
 * 任务队列管理器
 * 基于任务加入时机的动态处理，支持多队列管理
 * 特点：
 * - 每个队列同时只能有一个任务
 * - 新任务会顶替旧任务
 * - 支持延时执行和立即执行
 * - 自动清理过期任务
 */
export class TaskQueueManager {
  /** 队列存储，key为队列名称，value为任务数组 */
  private queues: Map<string, Task[]> = new Map();

  /** 定时器存储，key为队列名称，value为定时器对象 */
  private timers: Map<string, NodeJS.Timeout | null> = new Map();

  /**
   * 添加任务到指定队列
   * @param queueName 队列名称
   * @param task 任务对象（不包含id、timestamp、expireTime）
   * @description 如果队列不存在会自动创建，新任务会顶替队列中的旧任务
   */
  addTask(queueName: string, task: Omit<Task, 'id' | 'timestamp' | 'expireTime'>): void {
    // 动态创建队列，如果不存在则创建
    if (!this.queues.has(queueName)) {
      this.queues.set(queueName, []);
      this.timers.set(queueName, null);
    }

    const queue = this.queues.get(queueName);
    if (!queue) {
      return;
    }

    const currentTime = Date.now();
    const taskWithId: Task = {
      ...task,
      id: `${queueName}_${currentTime}_${Math.random().toString(36).substr(2, 9)}`,
      delay: task.delay,
    };

    // 清除现有定时器（如果有新任务加入，顶替原任务）
    this.stopProcessing(queueName);

    // 清空队列，新任务顶替原任务
    queue.length = 0;
    queue.push(taskWithId);

    // 如果有任务加入就执行任务处理流程
    this.processQueue(queueName);
  }

  /**
   * 处理指定队列
   * @param queueName 队列名称
   * @description 根据任务剩余延时时间决定立即执行还是延时执行
   */
  private processQueue(queueName: string): void {
    const queue = this.queues.get(queueName);
    if (!queue || queue.length === 0) {
      this.stopProcessing(queueName);
      return;
    }

    const task = queue[0]; // 队列中只有一个任务

    if (task.delay === null) {
      // delay为null，立即执行
      this.executeTaskAndRemove(queueName, task);
    } else {
      // delay不为null，则需等到delay时间后执行
      const timer = setTimeout(() => {
        this.executeTaskAndRemove(queueName, task);
      }, task.delay);

      this.timers.set(queueName, timer);
    }
  }

  /**
   * 停止队列处理
   * @param queueName 队列名称
   * @description 清除指定队列的定时器，停止任务执行
   */
  private stopProcessing(queueName: string): void {
    const timer = this.timers.get(queueName);
    if (timer) {
      clearTimeout(timer);
      this.timers.set(queueName, null);
    }
  }

  /**
   * 执行任务并从队列中移除
   * @param queueName 队列名称
   * @param task 要执行的任务
   * @description 执行完成后自动从队列移除
   */
  private async executeTaskAndRemove(queueName: string,
    task: Task): Promise<void> {
    try {
      // 立即执行
      await task.execute();
      // 任务执行完成后从队列中移除
      this.removeTaskFromQueue(queueName, task);
    } catch (error) {
      // 即使执行失败，也要从队列中移除任务
      this.removeTaskFromQueue(queueName, task);
    }
  }

  /**
   * 从队列中移除任务
   * @param queueName 队列名称
   * @param task 要移除的任务
   * @description 根据任务ID从指定队列中移除任务
   */
  private removeTaskFromQueue(queueName: string, task: Task): void {
    const queue = this.queues.get(queueName);
    if (queue) {
      const index = queue.findIndex((t) => t.id === task.id);
      if (index !== -1) {
        queue.splice(index, 1);
      }
    }
  }

  /**
   * 清理指定队列
   * @param queueName 队列名称
   * @description 清空队列中的所有任务并停止处理
   */
  clearQueue(queueName: string): void {
    const queue = this.queues.get(queueName);
    if (queue) {
      queue.length = 0;
    }
    this.stopProcessing(queueName);
  }

  /**
   * 销毁管理器，清理所有定时器
   * @description 清理所有队列和定时器，释放资源
   */
  destroy(): void {
    this.timers.forEach((timer, queueName) => {
      if (timer) {
        clearTimeout(timer);
        this.timers.set(queueName, null);
      }
    });
    this.queues.clear();
  }
}

// 创建全局任务队列管理器实例
export const taskQueueManager = new TaskQueueManager();
